import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { QRCodeSVG } from 'qrcode.react';
import { faPlus, faSpinner, faArrowLeft, faExchangeAlt, faTruck, faUndo, faWrench, faCheckCircle, faBan, faSearch } from '@fortawesome/free-solid-svg-icons';
import { apiJson, apiForm } from '../apiClient';
import { Panel } from '../../crm/components/ui/Panel';
import { KpiCard, KpiGrid } from '../../crm/components/ui/KpiCard';
import { Badge, StatusBadge } from '../../crm/components/ui/Badge';
import { Button } from '../../crm/components/ui/Button';
import { Sheet } from '../../crm/components/ui/Sheet';
import { EmptyState } from '../../crm/components/ui/EmptyState';

const apiPat = (path, opts) => apiJson(`/patrimonio${path}`, opts);
const apiEst = (path, opts) => apiJson(`/estoque${path}`, opts);
const apiClientes = (path, opts) => apiJson(`/clientes${path}`, opts);
const apiProj = (path, opts) => apiJson(path, opts);

const fmtBRL = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—';
};
const fmtDate = (d) => (d ? d.split('-').reverse().join('/') : '—');
const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString('pt-BR') : '—');

const OWNERSHIP_LABEL = { NOSSO: 'Nosso', CLIENTE: 'Do cliente', TERCEIRO: 'De terceiro' };
const FINALIDADE_LABEL = { PRODUCAO: 'Produção', CLIENTE: 'Cliente', TESTE: 'Teste', DESENVOLVIMENTO: 'Desenvolvimento', RESERVA: 'Reserva' };
const EVENTO_LABEL = {
  CRIADO: 'Cadastrado', INDIVIDUALIZADO: 'Individualizado de compra', TRANSFERENCIA: 'Transferência',
  ENTREGA: 'Entrega', RETORNO: 'Retorno ao estoque', MANUTENCAO_ABERTA: 'Manutenção aberta',
  MANUTENCAO_FECHADA: 'Manutenção fechada', STATUS_ALTERADO: 'Status alterado', BAIXADO: 'Baixado',
};
const STATUS_LABEL_FILTRO = { DISPONIVEL: 'Disponível', EM_USO: 'Em uso', INSTALADO: 'Instalado', EM_MANUTENCAO: 'Em manutenção', DEFEITUOSO: 'Defeituoso', BAIXADO: 'Baixado' };

// ─── Lista + KPIs ────────────────────────────────────────────────────────
function ListaPatrimonio({ onAbrirAsset, showToast }) {
  const [dashboard, setDashboard] = useState(null);
  const [assets, setAssets] = useState(null);
  const [busca, setBusca] = useState('');
  const [status, setStatus] = useState('');
  const [produtoFiltro, setProdutoFiltro] = useState('');
  const [localizacaoFiltro, setLocalizacaoFiltro] = useState('');
  const [produtos, setProdutos] = useState([]);
  const [localizacoes, setLocalizacoes] = useState([]);
  const [showNovoAtivo, setShowNovoAtivo] = useState(false);
  const [showGerarCompra, setShowGerarCompra] = useState(false);

  const carregarDashboard = useCallback(async () => { const r = await apiPat('/dashboard'); if (r.status === 'ok') setDashboard(r); }, []);
  const carregarAssets = useCallback(async () => {
    const params = new URLSearchParams();
    if (busca) params.set('busca', busca);
    if (status) params.set('status', status);
    if (produtoFiltro) params.set('produto_id', produtoFiltro);
    if (localizacaoFiltro) params.set('localizacao_id', localizacaoFiltro);
    const r = await apiPat(`/assets?${params}`);
    if (r.status === 'ok') setAssets(r.lista);
  }, [busca, status, produtoFiltro, localizacaoFiltro]);
  const carregarAuxiliares = useCallback(async () => {
    const [p, l] = await Promise.all([apiEst('/produtos?incluir_inativos=true'), apiEst('/localizacoes')]);
    if (p.status === 'ok') setProdutos(p.lista);
    if (l.status === 'ok') setLocalizacoes(l.lista);
  }, []);

  useEffect(() => { carregarDashboard(); carregarAuxiliares(); }, [carregarDashboard, carregarAuxiliares]);
  useEffect(() => { carregarAssets(); }, [carregarAssets]);

  const recarregarTudo = () => { carregarDashboard(); carregarAssets(); };
  const produtosRastreaveis = produtos.filter((p) => p.tipo_controle === 'RASTREAVEL');

  return (
    <div>
      {dashboard && (
        <KpiGrid>
          <KpiCard label="Ativos" value={dashboard.ativos} />
          <KpiCard label="Com clientes" value={dashboard.com_clientes} />
          <KpiCard label="Em manutenção" value={dashboard.em_manutencao} tone={dashboard.em_manutencao > 0 ? 'warning' : undefined} />
          <KpiCard label="Disponíveis" value={dashboard.disponiveis} />
          <KpiCard label="Defeituosos" value={dashboard.defeituosos} tone={dashboard.defeituosos > 0 ? 'danger' : undefined} />
        </KpiGrid>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', margin: 'var(--crm-space-4) 0', gap: 'var(--crm-space-2)', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 'var(--crm-space-2)', flexWrap: 'wrap' }}>
          <div className="crm-search-input" style={{ minWidth: '200px' }}>
            <FontAwesomeIcon icon={faSearch} />
            <input className="crm-input" placeholder="Buscar código/serial..." value={busca} onChange={(e) => setBusca(e.target.value)} />
          </div>
          <select className="crm-select" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">Status: Todos</option>
            {Object.entries(STATUS_LABEL_FILTRO).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select className="crm-select" value={produtoFiltro} onChange={(e) => setProdutoFiltro(e.target.value)}>
            <option value="">Produto: Todos</option>
            {produtos.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
          </select>
          <select className="crm-select" value={localizacaoFiltro} onChange={(e) => setLocalizacaoFiltro(e.target.value)}>
            <option value="">Local: Todos</option>
            {localizacoes.map((l) => <option key={l.id} value={l.id}>{l.nome}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', gap: 'var(--crm-space-2)' }}>
          <Button variant="outline" icon={faPlus} onClick={() => setShowNovoAtivo(true)}>Ativo</Button>
          <Button variant="primary" icon={faPlus} onClick={() => setShowGerarCompra(true)}>Gerar ativos de compra</Button>
        </div>
      </div>

      <Panel flush>
        {assets === null ? (
          <EmptyState title="Carregando..." />
        ) : assets.length === 0 ? (
          <EmptyState title="Nenhum ativo encontrado" actions={<Button variant="primary" icon={faPlus} onClick={() => setShowNovoAtivo(true)}>Ativo</Button>} />
        ) : (
          <div className="crm-table-wrap">
            <table className="crm-table">
              <thead><tr><th>Código</th><th>Equipamento</th><th>Status</th><th>Local/Cliente</th></tr></thead>
              <tbody>
                {assets.map((a) => (
                  <tr key={a.id} className="clickable" onClick={() => onAbrirAsset(a.id)}>
                    <td className="crm-table-primary">{a.asset_tag}</td>
                    <td>{a.produto_nome}{a.serial_number && <div className="crm-table-sub">S/N: {a.serial_number}</div>}</td>
                    <td><StatusBadge status={a.status} /></td>
                    <td>{a.current_stock_location_id ? a.current_stock_location_nome : (a.cliente_id ? 'Com cliente' : '—')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {showNovoAtivo && (
        <NovoAtivoModal produtos={produtosRastreaveis.length ? produtosRastreaveis : produtos} localizacoes={localizacoes}
          onFechar={() => setShowNovoAtivo(false)} onSalvo={() => { setShowNovoAtivo(false); recarregarTudo(); }} showToast={showToast} onProdutoCriado={carregarAuxiliares} />
      )}
      {showGerarCompra && <GerarDeCompraModal onFechar={() => setShowGerarCompra(false)} onSalvo={() => { setShowGerarCompra(false); recarregarTudo(); }} showToast={showToast} />}
    </div>
  );
}

// ─── Modal: Novo Ativo (avulso) — produto inline (§12) ─────────────────
function NovoAtivoModal({ produtos, localizacoes, onFechar, onSalvo, showToast, onProdutoCriado }) {
  const [listaProdutos, setListaProdutos] = useState(produtos);
  const [showNovoProduto, setShowNovoProduto] = useState(false);
  const [nomeNovoProduto, setNomeNovoProduto] = useState('');
  const [form, setForm] = useState({
    produto_id: '', asset_tag: '', serial_number: '', mac_address: '',
    ownership_type: 'NOSSO', finalidade: 'PRODUCAO', current_stock_location_id: '', garantia_ate: '',
  });
  const [salvando, setSalvando] = useState(false);

  const criarProdutoInline = async () => {
    if (!nomeNovoProduto.trim()) return;
    const r = await apiEst('/produtos', { method: 'POST', body: JSON.stringify({ nome: nomeNovoProduto, tipo_controle: 'RASTREAVEL' }) });
    if (r.status === 'ok') {
      showToast?.('Produto criado!', 'success');
      setListaProdutos([...listaProdutos, r.produto]);
      setForm({ ...form, produto_id: r.produto.id });
      setShowNovoProduto(false); setNomeNovoProduto('');
      onProdutoCriado?.();
    } else showToast?.(r.mensagem || 'Erro ao criar produto', 'error');
  };

  const salvar = async () => {
    if (!form.produto_id) { showToast?.('Selecione ou crie o produto', 'error'); return; }
    setSalvando(true);
    const body = { ...form };
    if (!body.current_stock_location_id) delete body.current_stock_location_id;
    if (!body.garantia_ate) delete body.garantia_ate;
    const result = await apiPat('/assets', { method: 'POST', body: JSON.stringify(body) });
    setSalvando(false);
    if (result.status === 'ok') { showToast?.('Ativo criado!', 'success'); onSalvo(); }
    else showToast?.(result.mensagem || 'Erro ao criar ativo', 'error');
  };

  return (
    <Sheet open onClose={onFechar} title="Novo Ativo" footer={<Button variant="primary" loading={salvando} onClick={salvar}>Criar Ativo</Button>}>
      <div className="crm-form-field">
        <label className="crm-form-label">Produto</label>
        <div style={{ display: 'flex', gap: 'var(--crm-space-2)' }}>
          <select className="crm-select" value={form.produto_id} onChange={(e) => setForm({ ...form, produto_id: e.target.value })} style={{ flex: 1 }}>
            <option value="">Selecione...</option>
            {listaProdutos.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
          </select>
          <Button size="sm" variant="outline" onClick={() => setShowNovoProduto(true)}>+ Criar produto</Button>
        </div>
        {showNovoProduto && (
          <div style={{ display: 'flex', gap: 'var(--crm-space-2)', marginTop: 'var(--crm-space-2)' }}>
            <input className="crm-input" placeholder="Nome do produto (rastreável)" value={nomeNovoProduto} onChange={(e) => setNomeNovoProduto(e.target.value)} />
            <Button size="sm" variant="primary" onClick={criarProdutoInline}>Salvar</Button>
          </div>
        )}
      </div>
      <div className="crm-form-field"><label className="crm-form-label">Código patrimonial (opcional — gera automático)</label><input className="crm-input" value={form.asset_tag} onChange={(e) => setForm({ ...form, asset_tag: e.target.value })} /></div>
      <div className="crm-form-field"><label className="crm-form-label">Serial (opcional)</label><input className="crm-input" value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} /></div>
      <div className="crm-form-field">
        <label className="crm-form-label">Propriedade</label>
        <select className="crm-select" value={form.ownership_type} onChange={(e) => setForm({ ...form, ownership_type: e.target.value })}>
          {Object.entries(OWNERSHIP_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>
      <div className="crm-form-field">
        <label className="crm-form-label">Finalidade</label>
        <select className="crm-select" value={form.finalidade} onChange={(e) => setForm({ ...form, finalidade: e.target.value })}>
          {Object.entries(FINALIDADE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>
      <div className="crm-form-field">
        <label className="crm-form-label">Local de estoque (vazio se já está fora do estoque)</label>
        <select className="crm-select" value={form.current_stock_location_id} onChange={(e) => setForm({ ...form, current_stock_location_id: e.target.value })}>
          <option value="">— fora do estoque —</option>
          {localizacoes.map((l) => <option key={l.id} value={l.id}>{l.nome}</option>)}
        </select>
      </div>
      <div className="crm-form-field"><label className="crm-form-label">Garantia até (opcional)</label><input className="crm-input" type="date" value={form.garantia_ate} onChange={(e) => setForm({ ...form, garantia_ate: e.target.value })} /></div>
    </Sheet>
  );
}

// ─── Modal: Gerar ativos a partir de uma compra ─────────────────────────
function GerarDeCompraModal({ onFechar, onSalvo, showToast }) {
  const [compras, setCompras] = useState(null);
  const [itemSelecionado, setItemSelecionado] = useState('');
  const [statusItem, setStatusItem] = useState(null);
  const [quantidade, setQuantidade] = useState('');
  const [salvando, setSalvando] = useState(false);

  useEffect(() => { apiEst('/compras').then((r) => { if (r.status === 'ok') setCompras(r.lista); }); }, []);
  const itensDisponiveis = (compras || []).flatMap((c) => (c.itens || []).map((it) => ({ ...it, compra_id: c.id, compra_data: c.data })));

  useEffect(() => {
    if (!itemSelecionado) { setStatusItem(null); return; }
    apiPat(`/purchase-items/${itemSelecionado}/status`).then((r) => { if (r.status === 'ok') setStatusItem(r); else setStatusItem(null); });
  }, [itemSelecionado]);

  const salvar = async () => {
    if (!itemSelecionado) { showToast?.('Selecione o item de compra', 'error'); return; }
    setSalvando(true);
    const body = { purchase_item_id: itemSelecionado };
    if (quantidade) body.quantidade = parseInt(quantidade, 10);
    const result = await apiPat('/assets/gerar-de-compra', { method: 'POST', body: JSON.stringify(body) });
    setSalvando(false);
    if (result.status === 'ok') { showToast?.(`${result.lista.length} ativo(s) gerado(s)!`, 'success'); onSalvo(); }
    else showToast?.(result.mensagem || 'Erro ao gerar ativos', 'error');
  };

  return (
    <Sheet open onClose={onFechar} title="Gerar Ativos de Compra" footer={<Button variant="primary" loading={salvando} disabled={!statusItem || statusItem.restante === 0 || statusItem.tipo_controle !== 'RASTREAVEL'} onClick={salvar}>Gerar Ativos</Button>}>
      {compras === null ? <div className="crm-form-hint">Carregando...</div> : itensDisponiveis.length === 0 ? (
        <div className="crm-form-hint">Nenhuma compra registrada ainda. Registre uma compra em Estoque primeiro.</div>
      ) : (
        <>
          <div className="crm-form-field">
            <label className="crm-form-label">Item de compra</label>
            <select className="crm-select" value={itemSelecionado} onChange={(e) => setItemSelecionado(e.target.value)}>
              <option value="">Selecione...</option>
              {itensDisponiveis.map((it) => <option key={it.id} value={it.id}>{fmtDate(it.compra_data)} — {it.quantidade}x (custo {fmtBRL(it.custo_unitario)})</option>)}
            </select>
          </div>
          {statusItem && (
            <div className="crm-form-hint">
              {statusItem.tipo_controle !== 'RASTREAVEL' ? 'Este produto não é rastreável — não é possível gerar ativos individuais.'
                : <>Comprado: {statusItem.quantidade_comprada} · Já individualizado: {statusItem.ja_individualizados} · Restante: <strong>{statusItem.restante}</strong></>}
            </div>
          )}
          <div className="crm-form-field"><label className="crm-form-label">Quantidade (vazio = todo o restante)</label><input className="crm-input" type="number" min="1" value={quantidade} onChange={(e) => setQuantidade(e.target.value)} /></div>
        </>
      )}
    </Sheet>
  );
}

// ─── Modal genérico de ação ──────────────────────────────────────────────
function AcaoModal({ titulo, campos, onFechar, onConfirmar, showToast, corPerigo }) {
  const [form, setForm] = useState(Object.fromEntries(campos.map((c) => [c.nome, c.valorInicial || ''])));
  const [salvando, setSalvando] = useState(false);

  const confirmar = async () => {
    for (const c of campos) if (c.obrigatorio && !form[c.nome]) { showToast?.(`Preencha: ${c.label}`, 'error'); return; }
    setSalvando(true);
    await onConfirmar(form);
    setSalvando(false);
  };

  return (
    <Sheet open onClose={onFechar} title={titulo} footer={<Button variant={corPerigo ? 'danger' : 'primary'} loading={salvando} onClick={confirmar}>Confirmar</Button>}>
      {campos.map((c) => (
        <div className="crm-form-field" key={c.nome}>
          <label className="crm-form-label">{c.label}</label>
          {c.tipo === 'select' ? (
            <select className="crm-select" value={form[c.nome]} onChange={(e) => setForm({ ...form, [c.nome]: e.target.value })}>
              <option value="">Selecione...</option>
              {c.opcoes.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          ) : (
            <input className="crm-input" type={c.tipo || 'text'} value={form[c.nome]} onChange={(e) => setForm({ ...form, [c.nome]: e.target.value })} />
          )}
        </div>
      ))}
    </Sheet>
  );
}

// ─── Documentos do ativo (§10.7, reaproveita domínio central) ───────────
function DocumentosAtivo({ assetId, showToast, onNavegar }) {
  const [documentos, setDocumentos] = useState(null);
  const [showNovo, setShowNovo] = useState(false);
  const [titulo, setTitulo] = useState('');
  const [arquivo, setArquivo] = useState(null);
  const [salvando, setSalvando] = useState(false);

  const carregar = useCallback(() => {
    const qs = new URLSearchParams({ entity_type: 'ATIVO', entity_id: assetId });
    apiProj(`/documentos?${qs}`).then((r) => { if (r.status === 'ok') setDocumentos(r.lista); });
  }, [assetId]);
  useEffect(() => { carregar(); }, [carregar]);

  const salvar = async () => {
    if (!titulo.trim() || !arquivo) { showToast?.('Informe o título e selecione um arquivo', 'error'); return; }
    setSalvando(true);
    const formData = new FormData();
    formData.append('titulo', titulo);
    formData.append('tipo', 'OUTRO');
    formData.append('arquivo', arquivo);
    formData.append('relacoes', JSON.stringify([{ entity_type: 'ATIVO', entity_id: assetId }]));
    const r = await apiForm('/documentos', { method: 'POST', body: formData });
    setSalvando(false);
    if (r.status === 'ok') { showToast?.('Documento adicionado!', 'success'); setShowNovo(false); setTitulo(''); setArquivo(null); carregar(); }
    else showToast?.(r.mensagem || 'Erro ao criar documento', 'error');
  };

  return (
    <Panel title="Documentos" actions={<Button size="sm" icon={faPlus} onClick={() => setShowNovo(true)}>Documento</Button>} flush>
      {documentos === null ? <div style={{ padding: 'var(--crm-space-4)' }}><FontAwesomeIcon icon={faSpinner} spin /></div> : documentos.length === 0 ? (
        <EmptyState title="Nenhum documento relacionado a este ativo" />
      ) : (
        <div className="crm-table-wrap">
          <table className="crm-table">
            <thead><tr><th>Título</th><th>Tipo</th><th>Atualizado</th></tr></thead>
            <tbody>{documentos.map((d) => (
              <tr key={d.id} className={onNavegar ? 'clickable' : ''} onClick={() => onNavegar?.('documentos', { documentoId: d.id })}>
                <td className="crm-table-primary">{d.titulo}</td><td><Badge tone="accent">{d.tipo}</Badge></td><td>{fmtDateTime(d.atualizado_em)}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
      <Sheet open={showNovo} onClose={() => setShowNovo(false)} title="Novo Documento" footer={<Button variant="primary" loading={salvando} onClick={salvar}>Salvar</Button>}>
        <div className="crm-form-field"><label className="crm-form-label">Título</label><input className="crm-input" value={titulo} onChange={(e) => setTitulo(e.target.value)} /></div>
        <div className="crm-form-field"><label className="crm-form-label">Arquivo</label><input type="file" onChange={(e) => setArquivo(e.target.files[0])} /></div>
      </Sheet>
    </Panel>
  );
}

// ─── Detalhe do ativo ────────────────────────────────────────────────────
function DetalheAtivo({ assetId, onVoltar, showToast, onNavegar }) {
  const [asset, setAsset] = useState(null);
  const [localizacoes, setLocalizacoes] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [projetos, setProjetos] = useState([]);
  const [acaoAtiva, setAcaoAtiva] = useState(null);

  const carregar = useCallback(async () => { const r = await apiPat(`/assets/${assetId}`); if (r.status === 'ok') setAsset(r.asset); }, [assetId]);

  useEffect(() => {
    carregar();
    apiEst('/localizacoes').then((r) => { if (r.status === 'ok') setLocalizacoes(r.lista); });
    apiClientes('').then((r) => { if (r.status === 'ok') setClientes(r.lista); });
    apiProj('/projetos').then((r) => { if (r.status === 'ok') setProjetos(r.lista || []); }).catch(() => {});
  }, [carregar]);

  if (!asset) return <div className="crm-form-hint"><FontAwesomeIcon icon={faSpinner} spin /> Carregando...</div>;

  const executar = async (path, body, mensagemSucesso) => {
    const result = await apiPat(path, { method: 'POST', body: JSON.stringify(body) });
    if (result.status === 'ok') { showToast?.(mensagemSucesso, 'success'); setAcaoAtiva(null); carregar(); }
    else showToast?.(result.mensagem || 'Erro na operação', 'error');
  };

  const manutencaoAberta = (asset.manutencoes || []).find((m) => m.status === 'ABERTA');
  const qrValue = `${window.location.origin}/crm/patrimonio/${asset.id}`;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--crm-space-4)', gap: 'var(--crm-space-3)', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--crm-space-3)' }}>
          <button className="crm-icon-btn" onClick={onVoltar}><FontAwesomeIcon icon={faArrowLeft} /></button>
          <div>
            <h2 style={{ margin: 0, fontSize: 'var(--crm-text-xl)', color: 'var(--crm-text-primary)' }}>{asset.asset_tag}</h2>
            <div className="crm-form-hint">{asset.produto_nome}</div>
          </div>
          <StatusBadge status={asset.status} />
        </div>
        <div style={{ display: 'flex', gap: 'var(--crm-space-2)', flexWrap: 'wrap' }}>
          {asset.current_stock_location_id && asset.status !== 'BAIXADO' && <Button size="sm" variant="outline" icon={faExchangeAlt} onClick={() => setAcaoAtiva('transferir')}>Transferir</Button>}
          {asset.current_stock_location_id && asset.status !== 'BAIXADO' && <Button size="sm" variant="outline" icon={faTruck} onClick={() => setAcaoAtiva('entregar')}>Entregar</Button>}
          {!asset.current_stock_location_id && asset.status !== 'BAIXADO' && <Button size="sm" variant="outline" icon={faUndo} onClick={() => setAcaoAtiva('retornar')}>Retornar</Button>}
          {asset.status !== 'EM_MANUTENCAO' && asset.status !== 'BAIXADO' && <Button size="sm" variant="outline" icon={faWrench} onClick={() => setAcaoAtiva('manutencao')}>Enviar manutenção</Button>}
          {asset.status === 'EM_MANUTENCAO' && manutencaoAberta && <Button size="sm" variant="outline" icon={faCheckCircle} onClick={() => setAcaoAtiva('fechar_manutencao')}>Finalizar manutenção</Button>}
          {asset.status !== 'BAIXADO' && <Button size="sm" variant="danger" icon={faBan} onClick={() => setAcaoAtiva('baixar')}>Baixar</Button>}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--crm-space-4)' }}>
        <KpiGrid>
          <KpiCard label="Propriedade" value={OWNERSHIP_LABEL[asset.ownership_type]} />
          <KpiCard label="Finalidade" value={FINALIDADE_LABEL[asset.finalidade]} />
          <KpiCard label="Local atual" value={asset.current_stock_location_nome || (asset.cliente_id ? 'Com cliente' : '—')} />
          <KpiCard label="Garantia" value={asset.garantia_ate ? `${fmtDate(asset.garantia_ate)} (${asset.garantia_status === 'EM_GARANTIA' ? 'em garantia' : 'vencida'})` : '—'} />
        </KpiGrid>

        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 'var(--crm-space-4)' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--crm-space-4)' }}>
            <Panel title="Identificação">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--crm-space-2)', fontSize: 'var(--crm-text-sm)' }}>
                <div>Serial: {asset.serial_number || '—'}</div>
                <div>MAC: {asset.mac_address || '—'}</div>
                <div>Custo aquisição: {fmtBRL(asset.custo_aquisicao)}</div>
                <div>Data aquisição: {fmtDate(asset.data_aquisicao)}</div>
                <div>Cliente: {asset.cliente_id ? (clientes.find((c) => c._id === asset.cliente_id)?.nome || asset.cliente_id.slice(0, 8)) : '—'}</div>
                <div>Projeto: {asset.projeto_id ? (projetos.find((p) => p.id === asset.projeto_id)?.nome || asset.projeto_id.slice(0, 8)) : '—'}</div>
              </div>
            </Panel>

            <Panel title="Histórico" flush>
              {(asset.historico || []).length === 0 ? <EmptyState title="Sem eventos ainda" /> : (
                <div className="crm-timeline">
                  {asset.historico.map((e) => (
                    <div className="crm-timeline-item" key={e.id}>
                      <span className="crm-timeline-desc">{EVENTO_LABEL[e.tipo] || e.tipo}{e.observacao ? ` — ${e.observacao}` : e.status_novo ? ` → ${e.status_novo}` : ''}</span>
                      <span className="crm-timeline-when">{fmtDateTime(e.ocorrido_em)}</span>
                    </div>
                  ))}
                </div>
              )}
            </Panel>

            <Panel title="Manutenção" flush>
              {(asset.manutencoes || []).length === 0 ? <EmptyState title="Nenhuma manutenção registrada" /> : (
                <div className="crm-table-wrap">
                  <table className="crm-table">
                    <thead><tr><th>Tipo</th><th>Status</th><th>Aberta</th><th>Fechada</th><th>Custo</th></tr></thead>
                    <tbody>
                      {asset.manutencoes.map((m) => (
                        <tr key={m.id}>
                          <td>{m.tipo}</td><td><Badge tone={m.status === 'ABERTA' ? 'warning' : 'positive'}>{m.status === 'ABERTA' ? 'Aberta' : 'Fechada'}</Badge></td>
                          <td>{fmtDateTime(m.aberto_em)}</td><td>{m.fechado_em ? fmtDateTime(m.fechado_em) : '—'}</td><td>{m.custo ? fmtBRL(m.custo) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Panel>

            <DocumentosAtivo assetId={asset.id} showToast={showToast} onNavegar={onNavegar} />
          </div>

          <Panel title="Identificação por QR">
            <div style={{ textAlign: 'center' }}>
              <div style={{ background: '#fff', display: 'inline-block', padding: 'var(--crm-space-3)', borderRadius: 'var(--crm-radius-md)' }}>
                <QRCodeSVG value={qrValue} size={160} />
              </div>
              <div className="crm-form-hint" style={{ marginTop: 'var(--crm-space-2)', wordBreak: 'break-all' }}>{qrValue}</div>
            </div>
          </Panel>
        </div>
      </div>

      {acaoAtiva === 'transferir' && (
        <AcaoModal titulo="Transferir Ativo" showToast={showToast} onFechar={() => setAcaoAtiva(null)}
          campos={[{ nome: 'localizacao_destino_id', label: 'Nova localização', tipo: 'select', obrigatorio: true, opcoes: localizacoes.filter((l) => l.id !== asset.current_stock_location_id).map((l) => ({ value: l.id, label: l.nome })) }]}
          onConfirmar={(f) => executar(`/assets/${asset.id}/transferir`, f, 'Ativo transferido!')} />
      )}
      {acaoAtiva === 'entregar' && (
        <AcaoModal titulo="Entregar Ativo" showToast={showToast} onFechar={() => setAcaoAtiva(null)}
          campos={[
            { nome: 'cliente_id', label: 'Cliente (opcional)', tipo: 'select', opcoes: clientes.map((c) => ({ value: c._id, label: c.nome })) },
            { nome: 'projeto_id', label: 'Projeto (opcional)', tipo: 'select', opcoes: projetos.map((p) => ({ value: p.id, label: p.nome })) },
            { nome: 'status', label: 'Status ao entregar', tipo: 'select', obrigatorio: true, valorInicial: 'EM_USO', opcoes: [{ value: 'EM_USO', label: 'Em uso' }, { value: 'INSTALADO', label: 'Instalado' }] },
          ]}
          onConfirmar={(f) => executar(`/assets/${asset.id}/entregar`, f, 'Ativo entregue!')} />
      )}
      {acaoAtiva === 'retornar' && (
        <AcaoModal titulo="Retornar ao Estoque" showToast={showToast} onFechar={() => setAcaoAtiva(null)}
          campos={[{ nome: 'localizacao_destino_id', label: 'Localização de destino', tipo: 'select', obrigatorio: true, opcoes: localizacoes.map((l) => ({ value: l.id, label: l.nome })) }]}
          onConfirmar={(f) => executar(`/assets/${asset.id}/retornar`, f, 'Ativo retornado ao estoque!')} />
      )}
      {acaoAtiva === 'manutencao' && (
        <AcaoModal titulo="Enviar para Manutenção" showToast={showToast} onFechar={() => setAcaoAtiva(null)}
          campos={[{ nome: 'tipo', label: 'Tipo/motivo', obrigatorio: true }, { nome: 'descricao', label: 'Descrição' }]}
          onConfirmar={(f) => executar(`/assets/${asset.id}/manutencao`, f, 'Manutenção aberta!')} />
      )}
      {acaoAtiva === 'fechar_manutencao' && manutencaoAberta && (
        <AcaoModal titulo="Finalizar Manutenção" showToast={showToast} onFechar={() => setAcaoAtiva(null)}
          campos={[{ nome: 'custo', label: 'Custo (opcional)', tipo: 'number' }, { nome: 'observacoes', label: 'Observações' }]}
          onConfirmar={(f) => executar(`/assets/${asset.id}/manutencao/${manutencaoAberta.id}/fechar`, f, 'Manutenção finalizada!')} />
      )}
      {acaoAtiva === 'baixar' && (
        <AcaoModal titulo="Baixar Ativo" corPerigo showToast={showToast} onFechar={() => setAcaoAtiva(null)}
          campos={[{ nome: 'motivo', label: 'Motivo da baixa', obrigatorio: true }]}
          onConfirmar={(f) => executar(`/assets/${asset.id}/baixar`, f, 'Ativo baixado.')} />
      )}
    </div>
  );
}

// ─── Root ────────────────────────────────────────────────────────────────
function Patrimonio({ showToast, ativoIdInicial, onNavegar }) {
  const [assetSelecionado, setAssetSelecionado] = useState(ativoIdInicial || null);
  if (assetSelecionado) {
    return <DetalheAtivo assetId={assetSelecionado} onVoltar={() => setAssetSelecionado(null)} showToast={showToast} onNavegar={onNavegar} />;
  }
  return <ListaPatrimonio onAbrirAsset={setAssetSelecionado} showToast={showToast} />;
}

export default Patrimonio;
